import {Plugins, EnvTypes, Expressions, Type} from '@packages/systems/wfdl';
import {
  EFunction,
  EUnion,
  ECase,
  functionOf,
  TGeneric,
  TTypeReference,
  constructTypeApplication,
  TText,
  TUnion,
  TEmptyRecord,
  EHole,
  TRecord,
  TList,
  ECall,
  TTypeApplication,
  TTypeConstructor,
  Expression,
} from '@packages/systems/wfdl-ast';
import {
  $,
  _$,
  forall,
  a,
  func,
  error,
  tyfun,
} from '@packages/utilities/wfdl-helpers';

import {
  TEffect,
  fail,
  LookupCMSIconMedium,
  CreateCMSIconMedium,
  DeleteCMSIconMedium,
  UpdateCMSIconMedium,
  CMSItemCreatedIconMedium,
  CMSItemDeletedIconMedium,
  CMSItemUpdatedIconMedium,
} from '@packages/systems/plugins/Effect';
import {TEmail, parseEmail} from '@packages/systems/plugins/Email';
import {TLink, parseLink} from '@packages/systems/plugins/Link';
import {TVideoLink, parseVideoLink} from '@packages/systems/plugins/Video';
import {TColor, parseColor} from '@packages/systems/plugins/Color';
import {SlugType} from './dataTypes/SlugAliasType';
import {BasicInfoFieldsType} from './dataTypes/BasicInfoFields';
import {ItemStatusDropdown} from './dataTypes/ItemStatusDropdown';
import {ItemCreators} from './dataTypes/ItemCreators';
import {ItemDeleters} from './dataTypes/ItemDeleters';
import {ItemIDDropdown} from './dataTypes/ItemIDDropdown';
import {CustomFieldsType} from './dataTypes/CustomFieldsType';
import {ItemSearchAliasType} from './dataTypes/ItemSearchAlias';
import {ItemFinders} from './dataTypes/ItemFinders';
import {UpdateItemStatus} from './dataTypes/UpdateItemStatus';
import {ItemUpdaters} from './dataTypes/ItemUpdaters';
import {ItemUpdatedTrigger} from './dataTypes/ItemUpdatedTrigger';
import {LOGIC_BLOCK_CATEGORIES} from '@packages/systems/flows/shared';
import {
  qualify,
  pluginName,
  typeName,
  iconName,
} from '@packages/systems/wfdl-Qualified';
import {hslToString} from '@packages/systems/core/utils/ColorUtils';
export * from './utils';
export {createSearchByType} from './dataTypes/SearchBy';
import {TJson} from '@packages/systems/plugins/Json';

const cmsParseErrorHandler = EFunction({
  param: 'error',
  body: fail(EUnion({tag: 'CMS Parse Error', value: $('error')})),
});

const createParser = (
  resultParser: (arg: Expression) => Expression,
  retTy: Type
) => ({
  envType: {
    dataType: TGeneric({
      arg: 'other errors',
      body: functionOf(
        TText,
        TEffect(
          TUnion(
            {
              'CMS Parse Error': TText,
            },
            TTypeReference('other errors')
          ),
          retTy
        )
      ),
    }),
    exported: 'hidden',
  },
  expression: EFunction({
    param: 'text',
    body: ECase({
      value: resultParser($('text')),
      cases: {
        Error: cmsParseErrorHandler,
        Ok: _$('Effect', 'pure'),
      },
    }),
  }),
});

const CHOOSE_EXISTING_ITEM = 'View items';
const ADD_ID_MANUALLY = 'Add item ID';

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    Slug: SlugType,
    'Basic Info Fields': BasicInfoFieldsType,
    ItemStatusDropdown,
    ItemCreators,
    'Custom Fields': CustomFieldsType,
    ItemDeleters,
    'Item Search': ItemSearchAliasType,
    'Item ID Dropdown': ItemIDDropdown,
    ItemFinders,
    UpdateItemStatus,
    ItemUpdaters,
    ItemUpdatedTrigger,
  },

  values: {
    'Item status to Text': {
      envType: {
        dataType: functionOf(
          TUnion({
            draft: TEmptyRecord,
            publish: TEmptyRecord,
            staged: TEmptyRecord,
          }),
          TText
        ),
        exported: 'visible',
        implicit: 'prefilledArgs',
      },
      // @ts-expect-error - TS7031 - Binding element 'tag' implicitly has an 'any' type.
      value: ({tag}) => tag,
    },
    'Update item status to Text': {
      envType: {
        dataType: functionOf(
          TUnion({
            current: TEmptyRecord,
            draft: TEmptyRecord,
            publish: TEmptyRecord,
            staged: TEmptyRecord,
            archived: TEmptyRecord,
          }),
          TText
        ),
        exported: 'visible',
        implicit: 'prefilledArgs',
      },
      // @ts-expect-error - TS7031 - Binding element 'tag' implicitly has an 'any' type.
      value: ({tag}) => tag,
    },
    'Expect Email': createParser(parseEmail, TEmail),
    'Expect Link': createParser(parseLink, TLink),
    'Expect Video Link': createParser(parseVideoLink, TVideoLink),
    'Expect Color': createParser(parseColor, TColor),

    'Create CMS item': {
      envType: {
        dataType: forall(
          ['error', 'a'],
          functionOf(
            constructTypeApplication({
              con: qualify(pluginName('CMS'), typeName('ItemCreators')),
              args: [error, a],
            }),
            TEffect(error, a)
          )
        ),
        exported: 'hidden',
      },
      expression: func(['x'], $('x')),
    },

    'Delete CMS item': {
      envType: {
        dataType: forall(
          ['error', 'a'],
          functionOf(
            constructTypeApplication({
              con: qualify(pluginName('CMS'), typeName('ItemDeleters')),
              args: [error, a],
            }),
            TEffect(error, a)
          )
        ),
        exported: 'hidden',
      },
      expression: func(['x'], $('x')),
    },

    'Update CMS item': {
      envType: {
        dataType: forall(
          ['error', 'a'],
          functionOf(
            constructTypeApplication({
              con: qualify(pluginName('CMS'), typeName('ItemUpdaters')),
              args: [error, a],
            }),
            TEffect(error, a)
          )
        ),
        exported: 'hidden',
      },
      expression: func(['x'], $('x')),
    },

    'Lookup CMS item': {
      envType: {
        dataType: forall(
          ['error', 'a'],
          functionOf(
            constructTypeApplication({
              con: qualify(pluginName('CMS'), typeName('ItemFinders')),
              args: [error, a],
            }),
            TEffect(error, a)
          )
        ),
        exported: 'hidden',
      },
      expression: func(['x'], $('x')),
    },

    collection_item_updated: {
      envType: {
        dataType: forall(
          ['error', 'a'],
          tyfun(
            [
              TTypeApplication({
                con: TTypeApplication({
                  con: TTypeConstructor(
                    qualify(Plugins.name('CMS'), typeName('ItemUpdatedTrigger'))
                  ),
                  arg: error,
                }),
                arg: a,
              }),
            ],

            tyfun([TJson], TEffect(error, a))
          )
        ),
        exported: 'hidden',
      },
      expression: func(['x'], $('x')),
    },

    'Item ID to Text': {
      envType: {
        dataType: functionOf(
          TUnion({
            [ADD_ID_MANUALLY]: TText,
            [CHOOSE_EXISTING_ITEM]: TRecord({name: TText, id: TText}),
          }),
          TText
        ),
        exported: 'visible',
        implicit: 'prefilledArgs',
      },
      // @ts-expect-error - TS7031 - Binding element 'tag' implicitly has an 'any' type. | TS7031 - Binding element 'val' implicitly has an 'any' type.
      value: ({tag, val}) => {
        if (tag === 'Add item ID') {
          return val;
        } else {
          return val.id;
        }
      },
    },

    'List to array of objects': {
      envType: {
        dataType: forall(
          ['a'],
          // This should be TList(TUnion(allFieldTypes)) - see `packages/systems/dynamo/wfdl-mappings`
          functionOf(TList(a), TList(TRecord({slug: TText, value: TText})))
        ),
        exported: 'hidden',
      },
      /** TODO - use field encoders instead to get the value, similar to Create CMS item */
      // @ts-expect-error - TS7006 - Parameter 'list' implicitly has an 'any' type.
      value: (list) => {
        // @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
        const arr = list.map((e) => {
          return {slug: e.tag, value: getValueFromPayload(e.val)};
        });
        return arr;
      },
    },
  } as {
    [key: string]: EnvTypes.TypedValue;
  },

  valuePresets: {
    createCMSItemFactory: {
      factory: Expressions._call(
        Expressions.qualifiedVariable('CMS', 'Create CMS item'),
        [EHole()]
      ),
      label: 'Create CMS item',
      icon: {
        medium: qualify(pluginName('CMS'), iconName('CreateCMSItemMedium')),
        small: qualify(pluginName('CMS'), iconName('CreateCMSItemSmall')),
      },
      category: LOGIC_BLOCK_CATEGORIES.Actions,
    },

    deleteCMSItemFactory: {
      factory: Expressions._call(
        Expressions.qualifiedVariable('CMS', 'Delete CMS item'),
        [EHole()]
      ),
      label: 'Delete CMS item',
      icon: {
        medium: qualify(pluginName('CMS'), iconName('DeleteCMSItemMedium')),
        small: qualify(pluginName('CMS'), iconName('DeleteCMSItemSmall')),
      },
      category: LOGIC_BLOCK_CATEGORIES.Actions,
    },

    updateCMSItemFactory: {
      factory: Expressions._call(
        Expressions.qualifiedVariable('CMS', 'Update CMS item'),
        [EHole()]
      ),
      label: 'Update CMS item',
      icon: {
        medium: qualify(pluginName('CMS'), iconName('UpdateCMSItemMedium')),
        small: qualify(pluginName('CMS'), iconName('UpdateCMSItemSmall')),
      },
      category: LOGIC_BLOCK_CATEGORIES.Actions,
    },

    itemUpdatedFactory: {
      factory: ECall({
        fun: Expressions.qualifiedVariable('CMS', 'collection_item_updated'),
        arg: EHole(),
      }),
      label: 'CMS item updated',
      icon: {
        medium: qualify(pluginName('CMS'), iconName('CMSItemUpdatedMedium')),
        small: qualify(pluginName('CMS'), iconName('CMSItemUpdatedSmall')),
      },
      helpData: {
        description: 'Runs every time a CMS item has been updated',
      },
      category: LOGIC_BLOCK_CATEGORIES.Triggers,
    },

    lookupCMSItemFactory: {
      factory: Expressions._call(
        Expressions.qualifiedVariable('CMS', 'Lookup CMS item'),
        [EHole()]
      ),
      label: 'Search for CMS items',
      icon: {
        medium: qualify(pluginName('CMS'), iconName('LookupCMSItemMedium')),
        small: qualify(pluginName('CMS'), iconName('LookupCMSItemSmall')),
      },
      category: LOGIC_BLOCK_CATEGORIES.Actions,
    },
  },

  icons: {
    CreateCMSItemMedium: CreateCMSIconMedium,
    CreateCMSItemSmall: CreateCMSIconMedium,
    DeleteCMSItemMedium: DeleteCMSIconMedium,
    DeleteCMSItemSmall: DeleteCMSIconMedium,
    UpdateCMSItemMedium: UpdateCMSIconMedium,
    UpdateCMSItemSmall: UpdateCMSIconMedium,
    LookupCMSItemMedium: LookupCMSIconMedium,
    LookupCMSItemSmall: LookupCMSIconMedium,
    LookupCMSBlock: LookupCMSIconMedium,

    CMSItemCreatedMedium: CMSItemCreatedIconMedium,
    CMSItemCreatedSmall: CMSItemCreatedIconMedium,
    CMSItemDeletedMedium: CMSItemDeletedIconMedium,
    CMSItemDeletedSmall: CMSItemDeletedIconMedium,
    CMSItemUpdatedMedium: CMSItemUpdatedIconMedium,
    CMSItemUpdatedSmall: CMSItemUpdatedIconMedium,
  },
});

export const expectEmailRef = _$('CMS', 'Expect Email');
export const expectLinkRef = _$('CMS', 'Expect Link');
export const expectVideoLinkRef = _$('CMS', 'Expect Video Link');
export const expectColorRef = _$('CMS', 'Expect Color');

/** Extracts the value from an Expression payload */
const getValueFromPayload = (value: any) => {
  // If it's just a string, we can return as-is
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    // If it's an object, but the value if a primitive, we can return it
    if (typeof value.val === 'string' || typeof value.val === 'number') {
      return value.val;
    }
    // Check if it's a color
    else if (
      value.val.h != null &&
      value.val.s != null &&
      value.val.l != null &&
      value.val.a != null
    ) {
      return hslToString(value.val);
    } else {
      return value.tag;
    }
  }
};
